import * as lodash from 'lodash';
import { DateTime } from 'luxon';
import { action, observable, toJS, makeObservable } from 'mobx';
import IFormValidator from '../classes/IFormValidator';
import { v4 as uuid } from 'uuid';
import * as yup from 'yup';
import IDeletionStack from '../classes/IDeletionStack';
import IFormWorkflow from '../classes/IFormWorkflow';
import { getSearchReportForm, getTitleEvidenceFeeType, ISearchReportFormData } from '../classes/getSearchReportForm';
import { SearchReportUiValidator } from '../validators/SearchReportUiValidator';
import { AppService } from './AppService';
import { EFetchState, NotificationService } from './NotificationService';
import {
  deleteItemAsync,
  finalizeAsync,
  getTitleReportAndCreateAsync,
  saveTitleReportAsync,
} from 'API/order/TitleReportsAPI';
import { UUID } from '../../types/shared/UUID';
import { Logger } from '../../ui-toolkit/logging/LoggerService';
import { createDefaultDeedData } from '../../ui-toolkit/orders/interfaces/titleReport/IDeed';
import { createDefaultMortgageData } from '../../ui-toolkit/orders/interfaces/titleReport/IMortgage';

export interface IDeletionStackItem {
  id: string;
  itemType: string;
}
export interface ISearchReportChange {
  index: number;
  dataPath: string;
  data: any;
}

export interface IHeaders {
  headers: {
    Authorization: string;
  };
}

export interface IItemTpl {
  createdAt: string;
  id: string;
}

export interface IDeletionStackItem {
  itemType: string;
  id: string;
}

export class SearchReportService implements IDeletionStack, IFormWorkflow, IFormValidator {
  public deletionStack: IDeletionStackItem[] = [];
  public savedIds: Set<string> = new Set('');

  public formDataValidator = SearchReportUiValidator(false);

  public formData = getSearchReportForm();
  public formDataChanged: boolean = false;
  public formErrors: object = {};

  private readonly appData: AppService;
  private notifications: NotificationService;

  constructor(appData: AppService, notifications: NotificationService) {
    makeObservable(this, {
      formData: observable,
      formDataChanged: observable,
      formErrors: observable,
      initValidator: action,
      setFormData: action,
      setFormDataChanged: action,
      setFormErrors: action,
      setPropVal: action,
      addNewItem: action,
      deleteItem: action,
      init: action,
      save: action,
      finalize: action,
    });

    this.appData = appData;
    this.notifications = notifications;
  }

  public initValidator = (isTitleEvidenceRequired: boolean) =>
    (this.formDataValidator = SearchReportUiValidator(isTitleEvidenceRequired));

  public setFormData = (newVal: ISearchReportFormData) => {
    const hasDeeds = newVal.deedInformation && !!newVal.deedInformation.length;
    const hasMortgage = newVal.mortgageInformation && !!newVal.mortgageInformation.length;

    if (!hasDeeds)
      newVal.deedInformation
        ? newVal.deedInformation.push(createDefaultDeedData())
        : (newVal.deedInformation = [createDefaultDeedData()]);
    if (!hasMortgage)
      newVal.mortgageInformation
        ? newVal.mortgageInformation.push(createDefaultMortgageData())
        : (newVal.mortgageInformation = [createDefaultMortgageData()]);

    this.formData = newVal;
    this.validateFormData();
  };

  public setFormDataChanged(newVal: boolean) {
    this.formDataChanged = newVal;
  }

  public setFormErrors(newVal: object = {}) {
    this.formErrors = newVal;
  }

  public removeFromDeletionStack = (id: string) => {
    this.deletionStack = this.deletionStack.filter((item) => item.id === id);
  };

  public addToDeletionStack = (item: IDeletionStackItem) => {
    const isOnStack = this.deletionStack.map((stackItem) => stackItem.id).includes(item.id);
    const isSaved = this.savedIds.has(item.id);

    if (!isOnStack && isSaved) {
      this.deletionStack.push(item);
    }
  };

  public resetDeletionStack = () => {
    this.deletionStack = [];
  };

  public validateFormData = async (dataPath?: string) => {
    const errors = await this.formDataValidator
      .validate(this.formData, { abortEarly: false })
      .then(() => undefined)
      .catch((err) => err);
    const hasErrors = errors && Array.isArray(errors.inner) && !!errors.inner.length;

    if (hasErrors) {
      let formErrors = { ...this.formErrors };
      if (dataPath) {
        const target = errors.inner.filter((e: yup.ValidationError) => e.path === dataPath)[0];
        if (target) {
          formErrors[dataPath] = target.message;
        } else {
          delete formErrors[dataPath];
        }
      } else {
        formErrors = errors.inner.reduce((acc: object, curr: yup.ValidationError) => {
          if ((dataPath !== undefined && dataPath === curr.path) || dataPath === undefined) {
            if (curr.path) acc[curr.path] = curr.message;
          }
          return acc;
        }, {});
        this.setFormErrors(formErrors);
        this.appData.setFinalizable(false);
      }
    } else {
      this.setFormErrors({});
      this.appData.setFinalizable(true);
    }

    return errors;
  };

  public getIsFormDataValid() {
    return this.formDataValidator.isValid(this.formData);
  }

  public setPropVal = (
    dataPath: string,
    newData: any,
    error?: string | null | undefined,
    targetData: ISearchReportFormData = { ...this.formData }
  ) => {
    const value = lodash.get(targetData, dataPath);

    if (Array.isArray(value) && newData.index !== undefined) {
      value[newData.index][newData.dataPath] = newData.data;
      lodash.set(targetData, dataPath, value);
      const arrayPath = `${dataPath}[${newData.index}].${newData.dataPath}`;
      this.validateFormData(arrayPath);
    } else {
      lodash.set(targetData, dataPath, newData);
      this.validateFormData(dataPath);
    }

    this.setFormData(targetData);
    this.setFormDataChanged(true);
    this.appData.setErrorMessage('');
  };

  public addNewItem = (
    dataPath: string,
    newItem: IItemTpl = this.generateNewItem(),
    target: ISearchReportFormData = { ...this.formData }
  ) => {
    const value = lodash.get(target, dataPath);
    const isValueEmpty = this.formData[dataPath] === null || this.formData[dataPath] === undefined;

    if (Array.isArray(value)) {
      value.push(newItem);
      this.setPropVal(dataPath, value);
    } else if (isValueEmpty) {
      this.setPropVal(dataPath, [newItem]);
    } else {
      throw new Error(`${dataPath} is not array`);
    }
  };

  public deleteItem = (
    dataPath: string,
    index?: number,
    targetData: ISearchReportFormData = { ...this.formData },
    targetErrors: object = { ...this.formErrors }
  ) => {
    const itemType = this.getApiTypeByDataPath(dataPath);
    const value = lodash.get(targetData, dataPath);
    let errorPaths: string[];

    if (!value && !value.id) throw new Error('No value to delete');

    const isValueArray = Array.isArray(value);
    const id = isValueArray && lodash.isNumber(index) ? value[index].id : value.id;

    if (isValueArray && lodash.isNumber(index)) {
      value.splice(index, 1);
      lodash.set(targetData, dataPath, value);
      this.addToDeletionStack({ itemType, id });
      errorPaths = Object.keys(this.formErrors).filter((key) => key.startsWith(dataPath));
    } else if (isValueArray) {
      lodash.set(targetData, dataPath, []);
      value.forEach((val: { id: string }) => this.addToDeletionStack({ itemType, id: val.id }));
      errorPaths = Object.keys(this.formErrors).filter((key) => key.startsWith(`${dataPath}[${index}]`));
    } else {
      throw new Error(`${dataPath} is neither array nor object with id prop`);
    }

    this.setFormData(targetData);

    errorPaths.forEach((path) => {
      delete targetErrors[path];
    });

    this.setFormErrors(targetErrors);
  };

  public init = async (orderId: UUID) => {
    this.setFormErrors();
    this.notifications.setSendState(EFetchState.START);
    await this.executeFetchSearchReport(orderId);
    this.notifications.setSendState(EFetchState.SUCCESS);
    this.setFormDataChanged(false);
  };

  public save = async (orderID: UUID) => {
    this.appData.setSaveAttempted(true);

    const errors = await this.validateFormData();
    if (errors) {
      this.appData.setErrorMessage('Please correct form errors before saving.');
      return;
    }

    this.notifications.resetAllButAuthState();
    this.notifications.setSaveState(EFetchState.START);
    await this.executeDelete(orderID);
    this.resetDeletionStack();
    await this.executeSave(orderID);
    this.appData.setSaveAttempted(false);
    this.notifications.setSaveState(EFetchState.SUCCESS);
  };

  public finalize = async (orderId: UUID) => {
    this.appData.setSaveAttempted(true);

    const errors = await this.validateFormData();
    if (errors) {
      this.appData.setErrorMessage('Please correct form errors before saving.');
      return;
    }

    this.notifications.resetAllButAuthState();
    this.notifications.setFinalizeState(EFetchState.START);
    await this.executeDelete(orderId);
    this.resetDeletionStack();
    await this.executeSave(orderId);
    await this.executeFinalize(orderId);
    this.appData.setSaveAttempted(false);
    this.notifications.setFinalizeState(EFetchState.SUCCESS);
  };

  private generateNewItem(): IItemTpl {
    return {
      createdAt: DateTime.local().toISO(),
      id: uuid(),
    };
  }

  private getApiTypeByDataPath(dataPath: string): string {
    switch (true) {
      case /mortgageInformation/.test(dataPath) && !/documents/.test(dataPath):
        return 'mortgages';
      case /documents/.test(dataPath):
        return 'mortgage_documents';
      case /judgements/.test(dataPath):
        return 'judgements';
      case /deedInformation/.test(dataPath):
        return 'deeds';
      default:
        return 'default'; // TODO: is it real API path part? should this throw?

      // General question. Are there collisions of ids beetwen API paths returned here? Is it safe to identify item only by id?
    }
  }

  private getAllDataIdsFlatSet(data: ISearchReportFormData): Set<string> {
    const toArrayOfIds = (theArray: string[], element: any | any[]): string[] => {
      if (Array.isArray(element)) {
        return theArray.concat(element.reduce(toArrayOfIds, []));
      }
      if (typeof element === 'object' && element !== null) {
        const { id, ...rest } = element;
        if (id) theArray.push(id);
        return theArray.concat(Object.values(rest).reduce(toArrayOfIds, []));
      }
      return theArray;
    };
    const ids = Object.keys(data)
      .map((t) => data[t])
      .reduce(toArrayOfIds, []);
    return new Set(ids);
  }

  private getPostSearchReportPayload() {
    let payload = toJS(this.formData);

    payload = this.handleInconsistentHoa(payload, true);
    // this is a place to add other handlers for inconsistencies between SearchReportUI data and POST:title_reports/v1/internal/REPORT_ID payload
    // payload = this...

    return payload;
  }

  private handleInconsistentHoa(formData: any, sending?: boolean) {
    // TODO: this WIP function, read comments below:
    // Due to a BE bug the key of "formData.summary.hoa" is returned as "formData.summary._hoa".
    // This need to be translated when receiving and when sending data.
    // Additional changes were requested here: https://trello.com/c/nncooZ1f/56-ui-hoa-section-a-change-to-what-we-display

    if (sending) {
      // sending
      const summary = { ...toJS(formData.summary) }; // lookout for mobx nested reference on summary
      summary._hoa = summary.hoa === 'yes' || (summary.hoa === 'no' ? false : null);
      delete summary.hoa;
      return {
        ...formData,
        summary,
      };
    }
    // receiving
    formData.summary.hoa = formData.summary._hoa ? 'yes' : formData.summary._hoa === false ? 'no' : 'to_be_determined';
    delete formData.summary._hoa;
    return formData;
  }

  /**
   * HTTP Calls executions
   */
  private executeDelete = (orderId: UUID) => {
    return Promise.all(
      this.deletionStack.map(({ itemType, id }) => {
        this.appData.setErrorMessage('');
        return deleteItemAsync(itemType, id)
          .then(() => this.removeFromDeletionStack(id))
          .catch((error) => {
            this.notifications.setSendState(EFetchState.FAILURE);
            this.appData.setErrorMessage('There was an error deleting items through Commitments Exceptions Service');
            const { token } = this.appData;
            Logger.error('Error deleting item', { orderId, token, itemType, itemId: id });
            throw new Error(error);
          });
      })
    );
  };

  private executeSave = (orderId: UUID) => {
    this.appData.setErrorMessage('');
    return saveTitleReportAsync(orderId, this.getPostSearchReportPayload())
      .then(() => {
        this.setFormDataChanged(false);
        this.savedIds = this.getAllDataIdsFlatSet(this.formData);
      })
      .catch((error) => {
        this.notifications.setSaveState(EFetchState.FAILURE);
        this.appData.setErrorMessage(`${error.request.status} ${error.request.statusText}`);
        const { token } = this.appData;
        Logger.error('Error saving search report', { orderId, token, formData: this.formData });
        throw new Error(error);
      });
  };

  private executeFinalize = (orderId: UUID) => {
    if (!this.appData.finalizable) return this.appData.setErrorMessage("Can't finalize. Please check entered data.");
    this.appData.setErrorMessage('');
    return finalizeAsync(orderId).catch((error) => {
      this.notifications.setFinalizeState(EFetchState.FAILURE);
      this.appData.setErrorMessage(`${error.request.status} ${error.request.statusText}`);
      const { token } = this.appData;
      Logger.error('Error finalizing search report', { orderId, token });
      throw new Error(error);
    });
  };

  private executeFetchSearchReport = (orderId: UUID) => {
    return getTitleReportAndCreateAsync(orderId)
      .then((response) => {
        const formData = this.handleInconsistentHoa(
          getSearchReportForm({ ...response, titleEvidenceFeeType: getTitleEvidenceFeeType(response) })
        );
        this.savedIds = this.getAllDataIdsFlatSet(formData);
        this.setFormData(getSearchReportForm(formData));
        this.setFormDataChanged(false);
      })
      .catch((error) => {
        console.log('error', error); // tslint:disable-line
        this.notifications.setSendState(EFetchState.FAILURE);
        this.appData.setErrorMessage(`${error.request.status} ${error.request.statusText}`);
        const { token } = this.appData;
        Logger.error('Error getting initial search report', { orderId, token });
        throw new Error(error);
      });
  };
}
